import { Box, Divider, Flex, Grid, IconButton, Image, Text, Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent, DrawerCloseButton, FormControl, FormLabel, Input, Button, FormErrorMessage, useDisclosure, Checkbox } from "@chakra-ui/react";
import { useState, useEffect } from "react";
import AddressDetailLogo from '../../../../../assets/images/AddressDetailLogo.svg';
import EditLogo from "../../../../../assets/images/EditLogo.svg";
import { EmployeePersonalInfo } from "./modal";
import { Controller, useForm } from "react-hook-form";
import { useFetch } from "../../../../../hooks/useFetch";
import { CustomToast } from "../../../../../components";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAuth } from "../../../../../hooks/useAuth";

interface Props {
    employeeId: number | null;
    personalDetail?: EmployeePersonalInfo;
    reload: () => void;
}

export interface IAddress {
    empId?: number | null;

    current_Zipcode: string;
    current_Country: string;
    current_State: string;
    current_City: string;
    current_Address: string;

    permanent_Zipcode: string;
    permanent_Country: string;
    permanent_State: string;
    permanent_City: string;
    permanent_Address: string;
}

const fetchAddressByPincode = async (pincode: string) => {
    if (!pincode || pincode.length < 6) return null;



    try {
        const res = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
        const data = await res.json();
        if (data[0].Status !== "Success") return null;
        const info = data[0].PostOffice[0];
        return { country: info.Country || "India", state: info.State, city: info.District };
    } catch {
        return null;
    }
};

const AddressDetails = ({ employeeId, personalDetail, reload }: Props) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { addToast } = CustomToast();
    const { fetchApi } = useFetch(addToast);
    const { hasPermission, loginObj } = useAuth()
    const [sameAsCurrent, setSameAsCurrent] = useState(false);

    const loggedInEmployeeId = loginObj?.employeeId;

    const isOwnProfile = employeeId === loggedInEmployeeId

    const AddressSchema = yup.object().shape({
        current_Zipcode: yup.string().required("Zipcode is required"),
        current_Country: yup.string().required("Country is required"),
        current_State: yup.string().required("State is required"),
        current_City: yup.string().required("City is required"),
        current_Address: yup.string().required("Current address is required"),

        permanent_Zipcode: yup.string().required("Zipcode is required"),
        permanent_Country: yup.string().required("Country is required"),
        permanent_State: yup.string().required("State is required"),
        permanent_City: yup.string().required("City is required"),
        permanent_Address: yup.string().required("Permanent address is required"),
    });

    const { control, handleSubmit, setValue, watch, formState: { errors, isSubmitting }, reset } = useForm<IAddress>({
        resolver: yupResolver(AddressSchema),
        defaultValues: {
            current_Zipcode: personalDetail?.current_Zipcode || "",
            current_Country: personalDetail?.current_Country || "",
            current_State: personalDetail?.current_State || "",
            current_City: personalDetail?.current_City || "",
            current_Address: personalDetail?.current_Address || "",

            permanent_Zipcode: personalDetail?.permanent_Zipcode || "",
            permanent_Country: personalDetail?.permanent_Country || "",
            permanent_State: personalDetail?.permanent_State || "",
            permanent_City: personalDetail?.permanent_City || "",
            permanent_Address: personalDetail?.permanent_Address || "",
        }
    });

    useEffect(() => {
        reset({
            current_Zipcode: personalDetail?.current_Zipcode || "",
            current_Country: personalDetail?.current_Country || "",
            current_State: personalDetail?.current_State || "",
            current_City: personalDetail?.current_City || "",
            current_Address: personalDetail?.current_Address || "",

            permanent_Zipcode: personalDetail?.permanent_Zipcode || "",
            permanent_Country: personalDetail?.permanent_Country || "",
            permanent_State: personalDetail?.permanent_State || "",
            permanent_City: personalDetail?.permanent_City || "",
            permanent_Address: personalDetail?.permanent_Address || "",
        })
    }, [personalDetail, reset])


    const handleCurrentPincodeBlur = async () => {
        const pin = watch("current_Zipcode");
        const result = await fetchAddressByPincode(pin);

        if (result) {
            setValue("current_Country", result.country);
            setValue("current_State", result.state);
            setValue("current_City", result.city);

            if (sameAsCurrent) {
                setValue("permanent_Zipcode", pin);
                setValue("permanent_Country", result.country);
                setValue("permanent_State", result.state);
                setValue("permanent_City", result.city);
            }
        }
    };


    const handlePermanentPincodeBlur = async () => {
        if (sameAsCurrent) return;
        const pin = watch("permanent_Zipcode");
        const result = await fetchAddressByPincode(pin);

        if (result) {
            setValue("permanent_Country", result.country);
            setValue("permanent_State", result.state);
            setValue("permanent_City", result.city);
        }
    };

    useEffect(() => {
        if (sameAsCurrent) {
            setValue("permanent_Zipcode", watch("current_Zipcode"));
            setValue("permanent_Country", watch("current_Country"));
            setValue("permanent_State", watch("current_State"));
            setValue("permanent_City", watch("current_City"));
            setValue("permanent_Address", watch("current_Address"));
        } else {
            setValue("permanent_Zipcode", "");
            setValue("permanent_Country", "");
            setValue("permanent_State", "");
            setValue("permanent_City", "");
            setValue("permanent_Address", "");
        }
    }, [sameAsCurrent, watch("current_Zipcode"), watch("current_Country"), watch("current_State"), watch("current_City"), watch("current_Address")]);


    const handleEditClick = () => {
        reset();
        setSameAsCurrent(false);
        onOpen();
    };

    const onSubmit = async (data: IAddress) => {
        data.empId = employeeId;
        const response = await fetchApi(`Hrlense_Employee/personalDetail/${employeeId}`, "PUT", data, null, "Address Details Updated");
        if (response) {
            reload();
            onClose();
        }
    };


    return (
        <>
            <Box pt={2} mb={6} className="main">
                <Flex justify="space-between" align="center" mb={2} pl={5} pr={5}>
                    <Flex align="center" gap={3}>
                        <Image src={AddressDetailLogo} />
                        <Text className="font-poppins text_sm text_medium col">
                            Address Details
                        </Text>
                    </Flex>

                    {(hasPermission("Employee", "UpdateEmployeeAddressDetail") || (hasPermission('Employee', 'UpdateOwnAddressDetail') && isOwnProfile)) && (
                        <IconButton aria-label="Edit" icon={<Image src={EditLogo} />} variant="none" onClick={handleEditClick} />
                    )}
                </Flex>

                <Box py={2} px={10} bg="#FFFFFF" borderRadius={8}>
                    <Grid templateColumns="1fr 1fr" gap={2}>
                        <Text className="font-poppins text_sm text_medium" color="rgba(102, 102, 102, 1)" mb={3}>Current Address</Text>
                        <Text className="font-poppins text_sm text_medium col">{personalDetail?.current_Address},{personalDetail?.current_City},{personalDetail?.current_State},{personalDetail?.current_Country} -{personalDetail?.current_Zipcode}</Text>
                        <Divider gridColumn="1 / -1" />

                        <Text className="font-poppins text_sm text_medium" color="rgba(102, 102, 102, 1)" mb={3}>Permanent Address</Text>
                        <Text className="font-poppins text_sm text_medium col"> {personalDetail?.permanent_Address}, {personalDetail?.permanent_City}, {personalDetail?.permanent_State}, {personalDetail?.permanent_Country} - {personalDetail?.permanent_Zipcode} </Text>
                    </Grid>
                </Box>
            </Box >

            {/* Drawer */}
            <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md" >
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerCloseButton />
                    <DrawerHeader>Update Address Details</DrawerHeader>
                    <DrawerBody>
                        <form onSubmit={handleSubmit(onSubmit)}>

                            {/* CURRENT ADDRESS */}
                            <Text fontWeight="bold" mb={3}>Current Address</Text>

                            <FormControl mb={3} isInvalid={!!errors.current_Zipcode}>
                                <FormLabel>Zipcode</FormLabel>
                                <Controller name="current_Zipcode" control={control} render={({ field }) => (<Input maxLength={6} {...field} onBlur={handleCurrentPincodeBlur} />)} />
                                <FormErrorMessage>{errors.current_Zipcode?.message}</FormErrorMessage>
                            </FormControl>

                            <FormControl mb={3} isInvalid={!!errors.current_Country}>
                                <FormLabel>Country</FormLabel>
                                <Controller name="current_Country" control={control} render={({ field }) => <Input {...field} />} />
                                <FormErrorMessage>{errors.current_Country?.message}</FormErrorMessage>
                            </FormControl>

                            <FormControl mb={3} isInvalid={!!errors.current_State}>
                                <FormLabel>State</FormLabel>
                                <Controller name="current_State" control={control} render={({ field }) => <Input {...field} />} />
                                <FormErrorMessage>{errors.current_State?.message}</FormErrorMessage>
                            </FormControl>

                            <FormControl mb={3} isInvalid={!!errors.current_City}>
                                <FormLabel>City / District</FormLabel>
                                <Controller name="current_City" control={control} render={({ field }) => <Input {...field} />} />
                                <FormErrorMessage>{errors.current_City?.message}</FormErrorMessage>
                            </FormControl>

                            <FormControl mb={5} isInvalid={!!errors.current_Address}>
                                <FormLabel>Current Address</FormLabel>
                                <Controller name="current_Address" control={control} render={({ field }) => <Input {...field} />} />
                                <FormErrorMessage>{errors.current_Address?.message}</FormErrorMessage>
                            </FormControl>

                            {/* SAME AS CURRENT */}
                            <Checkbox mb={4} isChecked={sameAsCurrent} onChange={(e) => setSameAsCurrent(e.target.checked)}> Same as Current Address </Checkbox>

                            {/* PERMANENT ADDRESS */}
                            <Text fontWeight="bold" mb={3} mt={2}>Permanent Address</Text>

                            <FormControl mb={3} isInvalid={!!errors.permanent_Zipcode}>
                                <FormLabel>Zipcode</FormLabel>
                                <Controller name="permanent_Zipcode" control={control} render={({ field }) => (<Input maxLength={6} {...field} isDisabled={sameAsCurrent} onBlur={handlePermanentPincodeBlur} />)} />
                                <FormErrorMessage>{errors.permanent_Zipcode?.message}</FormErrorMessage>
                            </FormControl>

                            <FormControl mb={3} isInvalid={!!errors.permanent_Country}>
                                <FormLabel>Country</FormLabel>
                                <Controller name="permanent_Country" control={control} render={({ field }) => (<Input {...field} isDisabled={sameAsCurrent} />)} />
                                <FormErrorMessage>{errors.permanent_Country?.message}</FormErrorMessage>
                            </FormControl>

                            <FormControl mb={3} isInvalid={!!errors.permanent_State}>
                                <FormLabel>State</FormLabel>
                                <Controller name="permanent_State" control={control} render={({ field }) => (<Input {...field} isDisabled={sameAsCurrent} />)} />
                                <FormErrorMessage>{errors.permanent_State?.message}</FormErrorMessage>
                            </FormControl>

                            <FormControl mb={3} isInvalid={!!errors.permanent_City}>
                                <FormLabel>City / District</FormLabel>
                                <Controller name="permanent_City" control={control} render={({ field }) => (<Input {...field} isDisabled={sameAsCurrent} />)} />
                                <FormErrorMessage>{errors.permanent_City?.message}</FormErrorMessage>
                            </FormControl>

                            <FormControl mb={5} isInvalid={!!errors.permanent_Address}>
                                <FormLabel>Permanent Address</FormLabel>
                                <Controller name="permanent_Address" control={control} render={({ field }) => (<Input {...field} isDisabled={sameAsCurrent} />)} />
                                <FormErrorMessage>{errors.permanent_Address?.message}</FormErrorMessage>
                            </FormControl>

                            <Box mt={6} textAlign="right">
                                <Button type="submit" className="btn_theme btn_theme:hover" isLoading={isSubmitting}>
                                    Update
                                </Button>
                            </Box>
                        </form>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </>
    );
};

export default AddressDetails;
